import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FolhaDeBaixo } from './FolhaDeBaixo'
import {
  ESPERA_DO_G_MS,
  IR_PARA,
  acaoDaTela,
  acoesDaTela,
  estaDigitando,
  proximaLinha,
  temModificador,
} from './teclas'

/**
 * O ouvinte de teclado do app inteiro. As regras estão em `teclas.ts`; este
 * componente só as liga ao `document` e desenha a folha de ajuda do `?`.
 *
 * ## Quando uma tecla NÃO é atalho
 *
 * - **Com Cmd, Ctrl ou Alt**: são do sistema e do navegador.
 * - **Digitando** (campo, `<select>`, texto editável): a tecla é texto. Esc
 *   ali tira o foco do campo, para os atalhos voltarem sem mouse.
 * - **Com diálogo aberto** (`aria-modal`): a folha e a tela cheia já cuidam
 *   do Esc e das setas delas, e um "voltar" disparado por baixo levaria a tela
 *   junto com a folha.
 * - **Com menu flutuante aberto** (`data-esc-proprio`), só o Esc: o menu se
 *   fecha sozinho, e a mesma tecla não pode também voltar de tela.
 *
 * ## Enter é tratado aqui, e não deixado ao navegador
 *
 * O navegador já ativa link e botão em foco com Enter. Fazer o mesmo aqui —
 * `click()` com `preventDefault` — dá o mesmo resultado sem clique duplo, e é
 * o que deixa o gesto verificável num teste: o jsdom não simula a ativação
 * nativa.
 */
export function Atalhos() {
  const navigate = useNavigate()
  const [ajuda, setAjuda] = useState(false)
  // Refs, e não estado: são lidos e escritos só dentro do handler, e redesenhar
  // a casca inteira a cada `g` seria trabalho por nada.
  const esperandoLetra = useRef(false)
  const prazoDoG = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    function cancelarG() {
      esperandoLetra.current = false
      if (prazoDoG.current !== null) clearTimeout(prazoDoG.current)
      prazoDoG.current = null
    }

    function aoTeclar(e: KeyboardEvent) {
      if (e.defaultPrevented || temModificador(e)) return
      if (document.querySelector('[aria-modal="true"]') !== null) return
      if (e.key === 'Escape' && document.querySelector('[data-esc-proprio]') !== null) return

      if (estaDigitando(e.target)) {
        if (e.key === 'Escape' && e.target instanceof HTMLElement) e.target.blur()
        return
      }

      if (esperandoLetra.current) {
        cancelarG()
        const destino = IR_PARA[e.key]
        if (destino !== undefined) {
          e.preventDefault()
          void navigate({ to: destino.rota })
        }
        return
      }

      const ativo = document.activeElement
      switch (e.key) {
        case 'g':
          esperandoLetra.current = true
          prazoDoG.current = setTimeout(cancelarG, ESPERA_DO_G_MS)
          return

        case 'ArrowDown':
        case 'ArrowUp': {
          const linhas = linhasNavegaveis()
          // Sem linha na tela, a seta continua rolando a página.
          if (linhas.length === 0) return
          e.preventDefault()
          const indice = linhas.findIndex((l) => l === ativo || (ativo !== null && l.contains(ativo)))
          const alvo = proximaLinha(linhas, indice, e.key === 'ArrowDown' ? 1 : -1, naTela)
          if (alvo === null) return
          alvo.focus({ preventScroll: true })
          trazerParaVista(alvo)
          return
        }

        case 'Enter':
          if (ativo instanceof HTMLElement && ativo.matches('[data-nav-linha]')) {
            e.preventDefault()
            ativo.click()
          }
          return

        case 'Escape': {
          // Do mais perto para o mais longe: fechar o que abriu na linha,
          // soltar a linha, e só então sair da tela.
          const expandida = document.querySelector<HTMLElement>(
            'main [data-nav-expansivel][aria-expanded="true"]',
          )
          if (expandida !== null) {
            expandida.click()
            expandida.focus({ preventScroll: true })
            return
          }
          if (ativo instanceof HTMLElement && ativo.matches('[data-nav-linha]')) {
            ativo.blur()
            return
          }
          document.querySelector<HTMLElement>('main [data-voltar]')?.click()
          return
        }

        case '/': {
          // Shift+/ é `?` na maioria dos teclados, mas nem todo sistema entrega
          // a tecla já traduzida — e aí ela chega como "/" com Shift.
          if (e.shiftKey) {
            e.preventDefault()
            setAjuda(true)
            return
          }
          const busca = document.querySelector<HTMLInputElement>('main [data-atalho-busca]')
          if (busca === null) return
          // Sem isto a barra entraria no campo que acabou de ganhar foco.
          e.preventDefault()
          busca.focus()
          busca.select()
          return
        }

        case 'n': {
          const novo = document.querySelector<HTMLElement>('main [data-atalho-novo]')
          if (novo === null) return
          e.preventDefault()
          // Campo recebe foco (o "O que falta?" dos lembretes); botão, clique.
          if (novo instanceof HTMLInputElement) novo.focus()
          else novo.click()
          return
        }

        case '?':
          e.preventDefault()
          setAjuda(true)
          return

        default: {
          const acao = acaoDaTela(e.key)
          if (acao === null) return
          e.preventDefault()
          acao.fazer()
        }
      }
    }

    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      cancelarG()
    }
  }, [navigate])

  if (!ajuda) return null
  return (
    <AjudaDoTeclado
      onFechar={() => {
        setAjuda(false)
      }}
    />
  )
}

/**
 * As linhas que a seta alcança agora.
 *
 * **Fora de dobra fechada**: as transações futuras moram num `<details>`
 * (decisão 288), e andar por elas sem vê-las seria focar o invisível. O jsdom
 * não mede layout, então a regra é pela estrutura, e não por `getClientRects`.
 */
function linhasNavegaveis(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>('main [data-nav-linha]')].filter(
    (el) =>
      el.closest('details:not([open])') === null &&
      el.closest('[hidden]') === null &&
      !(el instanceof HTMLButtonElement && el.disabled),
  )
}

/** Está pelo menos em parte dentro da janela? */
function naTela(el: HTMLElement): boolean {
  const caixa = el.getBoundingClientRect()
  return caixa.bottom > 0 && caixa.top < window.innerHeight
}

/** O jsdom não tem `scrollIntoView`. */
function trazerParaVista(el: HTMLElement) {
  const rolar = (el as Partial<Pick<HTMLElement, 'scrollIntoView'>>).scrollIntoView
  if (rolar !== undefined) rolar.call(el, { block: 'nearest' })
}

/**
 * O `?`: o que o teclado faz, e o que vale NESTA tela.
 *
 * A seção da tela é montada lendo as mesmas marcas que o ouvinte usa — assim a
 * ajuda não promete `/` numa tela sem busca.
 */
function AjudaDoTeclado({ onFechar }: { onFechar: () => void }) {
  // Lido uma vez, ao abrir: a folha cobre a tela, e nada embaixo muda enquanto
  // ela está aberta. Inicializador de estado, e não leitura no render.
  const [daTela] = useState(() => ({
    busca: document.querySelector('main [data-atalho-busca]') !== null,
    novo: document.querySelector('main [data-atalho-novo]') !== null,
    voltar: document.querySelector('main [data-voltar]') !== null,
    acoes: acoesDaTela(),
  }))

  return (
    <FolhaDeBaixo titulo="Atalhos do teclado" onFechar={onFechar}>
      <h2 className="titulo mb-2 px-1 text-base">Atalhos do teclado</h2>

      <Grupo titulo="Nas listas">
        <Tecla teclas={['↑', '↓']}>andar pelas linhas</Tecla>
        <Tecla teclas={['Enter']}>abrir a linha</Tecla>
        <Tecla teclas={['Esc']}>
          {daTela.voltar ? 'fechar, soltar a linha, ou voltar' : 'fechar ou soltar a linha'}
        </Tecla>
      </Grupo>

      {(daTela.busca || daTela.novo || daTela.acoes.length > 0) && (
        <Grupo titulo="Nesta tela">
          {daTela.busca && <Tecla teclas={['/']}>buscar</Tecla>}
          {daTela.novo && <Tecla teclas={['n']}>criar novo</Tecla>}
          {daTela.acoes.map((a) => (
            <Tecla key={a.tecla} teclas={[rotuloDaTecla(a.tecla)]}>
              {a.descricao}
            </Tecla>
          ))}
        </Grupo>
      )}

      <Grupo titulo="Ir para">
        {Object.entries(IR_PARA).map(([letra, destino]) => (
          <Tecla key={letra} teclas={['g', letra]}>
            {destino.nome}
          </Tecla>
        ))}
      </Grupo>

      <Grupo titulo="Sempre">
        <Tecla teclas={['?']}>esta ajuda</Tecla>
      </Grupo>

      <button
        type="button"
        onClick={onFechar}
        className="alvo mt-2 w-full rounded-lg border-t border-[var(--border)] text-sm text-[var(--text-muted)]"
      >
        Fechar
      </button>
    </FolhaDeBaixo>
  )
}

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-3 px-1">
      <h3 className="mb-1 text-xs text-[var(--text-muted)]">{titulo}</h3>
      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 text-sm">{children}</dl>
    </section>
  )
}

function Tecla({ teclas, children }: { teclas: string[]; children: ReactNode }) {
  return (
    <>
      <dt className="flex gap-1">
        {teclas.map((t) => (
          <kbd
            key={t}
            className="min-w-6 rounded-md border border-[var(--border)] px-1.5 text-center font-mono text-xs"
          >
            {t}
          </kbd>
        ))}
      </dt>
      <dd>{children}</dd>
    </>
  )
}

function rotuloDaTecla(tecla: string): string {
  return { ArrowLeft: '←', ArrowRight: '→', ArrowUp: '↑', ArrowDown: '↓' }[tecla] ?? tecla
}
